import type { FC } from 'react';
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';
import { DisclosureBody } from './creditConsent.styles';
import { CONSENT_MODAL_CLOSE_LABEL } from './creditConsent.constants';
import { parseEmphasis } from './creditConsent.utils';

export type ConsentDisclosureModalProps = {
    show: boolean;
    /** Used for the modal id and as the prefix for every data-testid in the modal. */
    id: string;
    header: string;
    /** Rendered one paragraph per entry. */
    paragraphs: string[];
    onClose: () => void;
    size?: 'small' | 'large';
};

/**
 * Read-only disclosure modal shared by the Terms and Conditions and Privacy Notice links on the
 * credit-consent box. Both designs are structurally identical — header, body copy, single Close
 * button — so they differ only by the content passed in.
 */
export const ConsentDisclosureModal: FC<ConsentDisclosureModalProps> = ({
    show,
    id,
    header,
    paragraphs,
    onClose,
    size = 'small'
}) => (
    <Modal
        size={size}
        id={id}
        data-testid={id}
        show={show}
        onHide={onClose}
        header={<Typography variant="h4">{header}</Typography>}
        footer={{
            primary: {
                action: onClose,
                label: CONSENT_MODAL_CLOSE_LABEL,
                customIdentifiers: { primaryButton: { dataTestId: `${id}-close-btn` } }
            }
        }}
    >
        <DisclosureBody data-testid={`${id}-body`}>
            {paragraphs.map((paragraph) => (
                <Typography key={paragraph} variant="body-md" tag="p">
                    {parseEmphasis(paragraph).map((segment) =>
                        segment.bold ? <strong key={segment.text}>{segment.text}</strong> : segment.text
                    )}
                </Typography>
            ))}
        </DisclosureBody>
    </Modal>
);
